import type { FromWorker, ToWorker, WorkerUpdate } from './protocol';
import type { ExperimentConfig, ManualEvent, RunStats, RunStatus, Sample, StopReason } from './types';

export type EngineState = 'loading' | 'ready' | 'failed';

export interface SimSnapshot {
  engine: EngineState;
  engineName: string;
  engineMessage: string | null;
  loadMs: number | null;
  playing: boolean;
  status: RunStatus;
  t: number;
  stopReason: StopReason | null;
  latest: Sample | null;
  stats: RunStats | null;
  manualEvents: ManualEvent[];
  config: ExperimentConfig | null;
  /** Bumped when the trace array changes (it is mutated in place for efficiency). */
  traceVersion: number;
  error: string | null;
}

export interface FinishedRun {
  config: ExperimentConfig;
  trace: Sample[];
  status: 'complete' | 'stopped';
  stopReason: StopReason | null;
  stats: RunStats;
  manualEvents: ManualEvent[];
  engine: string;
}

/** Wall-clock time considered per frame is capped so a slow frame never triggers a big catch-up. */
const MAX_FRAME_SECONDS = 0.05;

/**
 * UI-thread handle to the physics worker. Owns playback timing; the worker owns physics.
 * Exposes a subscribe/getSnapshot pair for React's useSyncExternalStore.
 */
export class SimClient {
  private worker: Worker | null = null;
  private seq = 0;
  private runToken = -1;
  private inFlight = false;
  private rafId: number | null = null;
  private lastFrame: number | null = null;
  private listeners = new Set<() => void>();
  private finishListeners = new Set<(run: FinishedRun) => void>();
  private reportedToken = -1;
  speed = 1;
  readonly trace: Sample[] = [];
  private snap: SimSnapshot = {
    engine: 'loading',
    engineName: '',
    engineMessage: null,
    loadMs: null,
    playing: false,
    status: 'ready',
    t: 0,
    stopReason: null,
    latest: null,
    stats: null,
    manualEvents: [],
    config: null,
    traceVersion: 0,
    error: null,
  };

  constructor(private readonly createWorker: () => Worker) {}

  start(): void {
    if (this.worker) return;
    try {
      this.worker = this.createWorker();
    } catch (err) {
      this.set({ engine: 'failed', engineMessage: `Could not start the physics worker: ${String(err)}` });
      return;
    }
    this.worker.onmessage = (ev: MessageEvent<FromWorker>) => this.onMessage(ev.data);
    this.worker.onerror = (ev) => {
      this.set({ engine: 'failed', engineMessage: `Physics worker error: ${ev.message || 'unknown error'}` });
      this.pause();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pause();
      });
    }
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = () => this.snap;

  onFinished(fn: (run: FinishedRun) => void) {
    this.finishListeners.add(fn);
    return () => this.finishListeners.delete(fn);
  }

  private set(patch: Partial<SimSnapshot>) {
    this.snap = { ...this.snap, ...patch };
    this.listeners.forEach((l) => l());
  }

  private send(msg: Omit<ToWorker, 'seq'> & { type: ToWorker['type'] }) {
    if (!this.worker) return;
    this.worker.postMessage({ ...msg, seq: ++this.seq } as ToWorker);
  }

  load(config: ExperimentConfig, replay?: ManualEvent[]) {
    this.pause();
    this.inFlight = false;
    this.trace.length = 0;
    this.set({ config, status: 'ready', t: 0, stopReason: null, latest: null, stats: null, manualEvents: [], error: null, traceVersion: this.snap.traceVersion + 1 });
    this.send({ type: 'load', config, replay } as ToWorker);
  }

  /**
   * Show a previously saved run in the live plots for inspection (no re-simulation, not re-recorded).
   * Reset / Replay start a fresh live experiment from the run’s settings and manual inputs.
   */
  viewFinishedRun(run: {
    config: ExperimentConfig;
    samples: Sample[];
    status: 'complete' | 'stopped';
    stopReason: StopReason | null;
    stats: RunStats;
    manualEvents: ManualEvent[];
    engine?: string;
  }) {
    this.pause();
    this.inFlight = false;
    // Ignore any in-flight worker updates until the next load().
    this.runToken = -1;
    this.trace.length = 0;
    for (const s of run.samples) this.trace.push(s);
    const latest = run.samples.length ? run.samples[run.samples.length - 1] : null;
    this.set({
      config: run.config,
      status: run.status,
      t: latest?.t ?? 0,
      stopReason: run.stopReason,
      latest,
      stats: run.stats,
      manualEvents: run.manualEvents.slice(),
      error: null,
      engineName: run.engine || this.snap.engineName,
      traceVersion: this.snap.traceVersion + 1,
    });
  }

  play() {
    if (this.snap.engine !== 'ready' || this.snap.status === 'complete' || this.snap.status === 'stopped') return;
    if (this.snap.playing) return;
    this.set({ playing: true });
    this.lastFrame = null;
    const frame = (now: number) => {
      if (!this.snap.playing) return;
      if (this.lastFrame !== null && !this.inFlight) {
        const wall = Math.min((now - this.lastFrame) / 1000, MAX_FRAME_SECONDS);
        this.inFlight = true;
        this.send({ type: 'advance', seconds: wall * this.speed } as ToWorker);
      }
      this.lastFrame = now;
      this.rafId = requestAnimationFrame(frame);
    };
    this.rafId = requestAnimationFrame(frame);
  }

  pause() {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    if (this.snap.playing) this.set({ playing: false });
  }

  step() {
    this.pause();
    this.send({ type: 'step' } as ToWorker);
  }

  runToEnd() {
    this.pause();
    this.send({ type: 'run-to-end' } as ToWorker);
  }

  setManual(value: number) {
    this.send({ type: 'manual', value } as ToWorker);
  }

  private onMessage(m: FromWorker) {
    switch (m.type) {
      case 'ready':
        this.set({ engine: 'ready', engineName: m.engine, loadMs: m.loadMs });
        break;
      case 'fatal':
        this.set({ engine: 'failed', engineMessage: m.message });
        this.pause();
        break;
      case 'error':
        this.inFlight = false;
        this.pause();
        this.set({ error: m.message });
        break;
      case 'update':
        this.applyUpdate(m);
        break;
    }
  }

  private applyUpdate(m: WorkerUpdate) {
    this.inFlight = false;
    if (m.reset) this.runToken = m.runToken;
    if (m.runToken !== this.runToken) return; // stale message from a previous run
    if (m.reset) this.trace.length = 0;
    for (const s of m.samples) this.trace.push(s);
    const done = m.status === 'complete' || m.status === 'stopped';
    this.set({
      status: m.status,
      t: m.t,
      stopReason: m.stopReason,
      latest: m.latest,
      stats: m.stats,
      manualEvents: m.manualEvents,
      traceVersion: this.snap.traceVersion + 1,
    });
    if (done) {
      this.pause();
      if (this.reportedToken !== m.runToken && this.snap.config) {
        this.reportedToken = m.runToken;
        const run: FinishedRun = {
          config: this.snap.config,
          trace: this.trace.slice(),
          status: m.status as 'complete' | 'stopped',
          stopReason: m.stopReason,
          stats: m.stats,
          manualEvents: m.manualEvents,
          engine: this.snap.engineName,
        };
        this.finishListeners.forEach((l) => l(run));
      }
    }
  }
}

export const simClient = new SimClient(() => new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' }));
